/**
 * DS-Wiki API Service
 * Handles all API calls for the DS-Wiki Knowledge Share Module
 */

import { BaseAPIService } from '../../../../services/baseapiservice';
import type {
  ApiResponse,
  Department,
  DepartmentInput,
  ArticleCategory,
  ArticleCategoryInput,
  Tag,
  TagInput,
  Workspace,
  WorkspaceInput,
  Location,
  LocationInput,
  ArticleType,
  ArticleTypeInput,
  ArticleStatus,
  ArticleStatusInput,
  SectionType,
  SectionTypeInput,
  SuggestionType,
  SuggestionTypeInput,
  Curator,
  CuratorInput,
  AccessLevel,
  AccessLevelInput,
  HealthCheckResponse,
} from './types';

class DSWikiService extends BaseAPIService {
  private basePath = 'ds-wiki';

  // ============================================================================
  // HEALTH CHECK
  // ============================================================================

  /**
   * Check if DS-Wiki API is running
   */
  async healthCheck(): Promise<HealthCheckResponse> {
    const response = await this.get(`${this.basePath}/health`);
    return response.data;
  }

  // ============================================================================
  // DEPARTMENT APIs
  // ============================================================================

  /**
   * Get all departments
   */
  async getAllDepartments(): Promise<ApiResponse<Department[]>> {
    const response = await this.get(`${this.basePath}/department`);
    return response.data;
  }

  /**
   * Get active departments only
   */
  async getActiveDepartments(): Promise<ApiResponse<Department[]>> {
    const response = await this.get(`${this.basePath}/department/active`);
    return response.data;
  }

  /**
   * Get department by ID
   */
  async getDepartmentById(id: number): Promise<ApiResponse<Department>> {
    const response = await this.get(`${this.basePath}/department/${id}`);
    return response.data;
  }

  /**
   * Get department by code
   */
  async getDepartmentByCode(code: string): Promise<ApiResponse<Department>> {
    const response = await this.get(`${this.basePath}/department/code/${code}`);
    return response.data;
  }

  /**
   * Create or update department
   */
  async saveDepartment(data: DepartmentInput): Promise<ApiResponse<Department>> {
    const response = await this.post(`${this.basePath}/department`, data);
    return response.data;
  }

  /**
   * Delete department
   */
  async deleteDepartment(id: number): Promise<ApiResponse<Department>> {
    const response = await this.delete(`${this.basePath}/department/${id}`);
    return response.data;
  }

  // ============================================================================
  // ARTICLE CATEGORY APIs
  // ============================================================================

  /**
   * Get all article categories
   */
  async getAllArticleCategories(): Promise<ApiResponse<ArticleCategory[]>> {
    const response = await this.get(`${this.basePath}/article-category`);
    return response.data;
  }

  /**
   * Get active article categories only
   */
  async getActiveArticleCategories(): Promise<ApiResponse<ArticleCategory[]>> {
    const response = await this.get(`${this.basePath}/article-category/active`);
    return response.data;
  }

  /**
   * Get article category by ID
   */
  async getArticleCategoryById(id: number): Promise<ApiResponse<ArticleCategory>> {
    const response = await this.get(`${this.basePath}/article-category/${id}`);
    return response.data;
  }

  /**
   * Get article category by code
   */
  async getArticleCategoryByCode(code: string): Promise<ApiResponse<ArticleCategory>> {
    const response = await this.get(`${this.basePath}/article-category/code/${code}`);
    return response.data;
  }

  /**
   * Create or update article category
   */
  async saveArticleCategory(data: ArticleCategoryInput): Promise<ApiResponse<ArticleCategory>> {
    const response = await this.post(`${this.basePath}/article-category`, data);
    return response.data;
  }

  /**
   * Delete article category
   */
  async deleteArticleCategory(id: number): Promise<ApiResponse<ArticleCategory>> {
    const response = await this.delete(`${this.basePath}/article-category/${id}`);
    return response.data;
  }

  // ============================================================================
  // TAG APIs
  // ============================================================================

  /**
   * Get all tags
   */
  async getAllTags(): Promise<ApiResponse<Tag[]>> {
    const response = await this.get(`${this.basePath}/tag`);
    return response.data;
  }

  /**
   * Get active tags only
   */
  async getActiveTags(): Promise<ApiResponse<Tag[]>> {
    const response = await this.get(`${this.basePath}/tag/active`);
    return response.data;
  }

  /**
   * Get tag by ID
   */
  async getTagById(id: number): Promise<ApiResponse<Tag>> {
    const response = await this.get(`${this.basePath}/tag/${id}`);
    return response.data;
  }

  /**
   * Get tag by name
   */
  async getTagByName(name: string): Promise<ApiResponse<Tag>> {
    const response = await this.get(`${this.basePath}/tag/name/${encodeURIComponent(name)}`);
    return response.data;
  }

  /**
   * Create or update tag
   */
  async saveTag(data: TagInput): Promise<ApiResponse<Tag>> {
    const response = await this.post(`${this.basePath}/tag`, data);
    return response.data;
  }

  /**
   * Delete tag
   */
  async deleteTag(id: number): Promise<ApiResponse<Tag>> {
    const response = await this.delete(`${this.basePath}/tag/${id}`);
    return response.data;
  }

  // ============================================================================
  // WORKSPACE APIs
  // ============================================================================

  /**
   * Get all workspaces
   */
  async getAllWorkspaces(): Promise<ApiResponse<Workspace[]>> {
    const response = await this.get(`${this.basePath}/workspace`);
    return response.data;
  }

  /**
   * Get active workspaces only
   */
  async getActiveWorkspaces(): Promise<ApiResponse<Workspace[]>> {
    const response = await this.get(`${this.basePath}/workspace/active`);
    return response.data;
  }

  /**
   * Get default workspace
   */
  async getDefaultWorkspace(): Promise<ApiResponse<Workspace | null>> {
    const response = await this.get(`${this.basePath}/workspace/default`);
    return response.data;
  }

  /**
   * Get workspace by ID
   */
  async getWorkspaceById(id: number): Promise<ApiResponse<Workspace>> {
    const response = await this.get(`${this.basePath}/workspace/${id}`);
    return response.data;
  }

  /**
   * Get workspace by code
   */
  async getWorkspaceByCode(code: string): Promise<ApiResponse<Workspace>> {
    const response = await this.get(`${this.basePath}/workspace/code/${code}`);
    return response.data;
  }

  /**
   * Create or update workspace
   */
  async saveWorkspace(data: WorkspaceInput): Promise<ApiResponse<Workspace>> {
    const response = await this.post(`${this.basePath}/workspace`, data);
    return response.data;
  }

  /**
   * Delete workspace
   */
  async deleteWorkspace(id: number): Promise<ApiResponse<Workspace>> {
    const response = await this.delete(`${this.basePath}/workspace/${id}`);
    return response.data;
  }

  // ============================================================================
  // LOCATION APIs
  // ============================================================================

  /**
   * Get all locations
   */
  async getAllLocations(): Promise<ApiResponse<Location[]>> {
    const response = await this.get(`${this.basePath}/location`);
    return response.data;
  }

  /**
   * Get active locations only
   */
  async getActiveLocations(): Promise<ApiResponse<Location[]>> {
    const response = await this.get(`${this.basePath}/location/active`);
    return response.data;
  }

  /**
   * Get location by ID
   */
  async getLocationById(id: number): Promise<ApiResponse<Location>> {
    const response = await this.get(`${this.basePath}/location/${id}`);
    return response.data;
  }

  /**
   * Get locations by type
   */
  async getLocationsByType(typeCode: string): Promise<ApiResponse<Location[]>> {
    const response = await this.get(`${this.basePath}/location/type/${typeCode}`);
    return response.data;
  }

  /**
   * Create or update location
   */
  async saveLocation(data: LocationInput): Promise<ApiResponse<Location>> {
    const response = await this.post(`${this.basePath}/location`, data);
    return response.data;
  }

  /**
   * Delete location
   */
  async deleteLocation(id: number): Promise<ApiResponse<Location>> {
    const response = await this.delete(`${this.basePath}/location/${id}`);
    return response.data;
  }

  // ============================================================================
  // ARTICLE TYPE APIs
  // ============================================================================

  /**
   * Get all article types
   */
  async getAllArticleTypes(): Promise<ApiResponse<ArticleType[]>> {
    const response = await this.get(`${this.basePath}/article-type`);
    return response.data;
  }

  /**
   * Get active article types only
   */
  async getActiveArticleTypes(): Promise<ApiResponse<ArticleType[]>> {
    const response = await this.get(`${this.basePath}/article-type/active`);
    return response.data;
  }

  /**
   * Get article type by ID
   */
  async getArticleTypeById(id: number): Promise<ApiResponse<ArticleType>> {
    const response = await this.get(`${this.basePath}/article-type/${id}`);
    return response.data;
  }

  /**
   * Get article type by code
   */
  async getArticleTypeByCode(code: string): Promise<ApiResponse<ArticleType>> {
    const response = await this.get(`${this.basePath}/article-type/code/${code}`);
    return response.data;
  }

  /**
   * Create or update article type
   */
  async saveArticleType(data: ArticleTypeInput): Promise<ApiResponse<ArticleType>> {
    const response = await this.post(`${this.basePath}/article-type`, data);
    return response.data;
  }

  /**
   * Delete article type
   */
  async deleteArticleType(id: number): Promise<ApiResponse<ArticleType>> {
    const response = await this.delete(`${this.basePath}/article-type/${id}`);
    return response.data;
  }

  // ============================================================================
  // ARTICLE STATUS APIs
  // ============================================================================

  /**
   * Get all article statuses
   */
  async getAllArticleStatuses(): Promise<ApiResponse<ArticleStatus[]>> {
    const response = await this.get(`${this.basePath}/article-status`);
    return response.data;
  }

  /**
   * Get active article statuses only
   */
  async getActiveArticleStatuses(): Promise<ApiResponse<ArticleStatus[]>> {
    const response = await this.get(`${this.basePath}/article-status/active`);
    return response.data;
  }

  /**
   * Get article status by ID
   */
  async getArticleStatusById(id: number): Promise<ApiResponse<ArticleStatus>> {
    const response = await this.get(`${this.basePath}/article-status/${id}`);
    return response.data;
  }

  /**
   * Get article status by code
   */
  async getArticleStatusByCode(code: string): Promise<ApiResponse<ArticleStatus>> {
    const response = await this.get(`${this.basePath}/article-status/code/${code}`);
    return response.data;
  }

  /**
   * Create or update article status
   */
  async saveArticleStatus(data: ArticleStatusInput): Promise<ApiResponse<ArticleStatus>> {
    const response = await this.post(`${this.basePath}/article-status`, data);
    return response.data;
  }

  /**
   * Delete article status
   */
  async deleteArticleStatus(id: number): Promise<ApiResponse<ArticleStatus>> {
    const response = await this.delete(`${this.basePath}/article-status/${id}`);
    return response.data;
  }

  // ============================================================================
  // SECTION TYPE APIs
  // ============================================================================

  /**
   * Get all section types
   */
  async getAllSectionTypes(): Promise<ApiResponse<SectionType[]>> {
    const response = await this.get(`${this.basePath}/section-type`);
    return response.data;
  }

  /**
   * Get active section types only
   */
  async getActiveSectionTypes(): Promise<ApiResponse<SectionType[]>> {
    const response = await this.get(`${this.basePath}/section-type/active`);
    return response.data;
  }

  /**
   * Get section type by ID
   */
  async getSectionTypeById(id: number): Promise<ApiResponse<SectionType>> {
    const response = await this.get(`${this.basePath}/section-type/${id}`);
    return response.data;
  }

  /**
   * Get section type by code
   */
  async getSectionTypeByCode(code: string): Promise<ApiResponse<SectionType>> {
    const response = await this.get(`${this.basePath}/section-type/code/${code}`);
    return response.data;
  }

  /**
   * Create or update section type
   */
  async saveSectionType(data: SectionTypeInput): Promise<ApiResponse<SectionType>> {
    const response = await this.post(`${this.basePath}/section-type`, data);
    return response.data;
  }

  /**
   * Delete section type
   */
  async deleteSectionType(id: number): Promise<ApiResponse<SectionType>> {
    const response = await this.delete(`${this.basePath}/section-type/${id}`);
    return response.data;
  }

  // ============================================================================
  // SUGGESTION TYPE APIs
  // ============================================================================

  /**
   * Get all suggestion types
   */
  async getAllSuggestionTypes(): Promise<ApiResponse<SuggestionType[]>> {
    const response = await this.get(`${this.basePath}/suggestion-type`);
    return response.data;
  }

  /**
   * Get active suggestion types only
   */
  async getActiveSuggestionTypes(): Promise<ApiResponse<SuggestionType[]>> {
    const response = await this.get(`${this.basePath}/suggestion-type/active`);
    return response.data;
  }

  /**
   * Get suggestion type by ID
   */
  async getSuggestionTypeById(id: number): Promise<ApiResponse<SuggestionType>> {
    const response = await this.get(`${this.basePath}/suggestion-type/${id}`);
    return response.data;
  }

  /**
   * Get suggestion type by code
   */
  async getSuggestionTypeByCode(code: string): Promise<ApiResponse<SuggestionType>> {
    const response = await this.get(`${this.basePath}/suggestion-type/code/${code}`);
    return response.data;
  }

  /**
   * Create or update suggestion type
   */
  async saveSuggestionType(data: SuggestionTypeInput): Promise<ApiResponse<SuggestionType>> {
    const response = await this.post(`${this.basePath}/suggestion-type`, data);
    return response.data;
  }

  /**
   * Delete suggestion type
   */
  async deleteSuggestionType(id: number): Promise<ApiResponse<SuggestionType>> {
    const response = await this.delete(`${this.basePath}/suggestion-type/${id}`);
    return response.data;
  }

  // ============================================================================
  // CURATOR APIs
  // ============================================================================

  /**
   * Get all curators
   */
  async getAllCurators(): Promise<ApiResponse<Curator[]>> {
    const response = await this.get(`${this.basePath}/curator`);
    return response.data;
  }

  /**
   * Get active curators only
   */
  async getActiveCurators(): Promise<ApiResponse<Curator[]>> {
    const response = await this.get(`${this.basePath}/curator/active`);
    return response.data;
  }

  /**
   * Get senior curators
   */
  async getSeniorCurators(): Promise<ApiResponse<Curator[]>> {
    const response = await this.get(`${this.basePath}/curator/senior`);
    return response.data;
  }

  /**
   * Get curator by ID
   */
  async getCuratorById(id: number): Promise<ApiResponse<Curator>> {
    const response = await this.get(`${this.basePath}/curator/${id}`);
    return response.data;
  }

  /**
   * Get curator by user ID
   */
  async getCuratorByUserId(userId: number): Promise<ApiResponse<Curator>> {
    const response = await this.get(`${this.basePath}/curator/user/${userId}`);
    return response.data;
  }

  /**
   * Get curators by department
   */
  async getCuratorsByDepartment(dptId: number): Promise<ApiResponse<Curator[]>> {
    const response = await this.get(`${this.basePath}/curator/department/${dptId}`);
    return response.data;
  }

  /**
   * Create or update curator
   */
  async saveCurator(data: CuratorInput): Promise<ApiResponse<Curator>> {
    const response = await this.post(`${this.basePath}/curator`, data);
    return response.data;
  }

  /**
   * Delete curator
   */
  async deleteCurator(id: number): Promise<ApiResponse<Curator>> {
    const response = await this.delete(`${this.basePath}/curator/${id}`);
    return response.data;
  }

  // ============================================================================
  // ACCESS LEVEL APIs
  // ============================================================================

  /**
   * Get all access levels
   */
  async getAllAccessLevels(): Promise<ApiResponse<AccessLevel[]>> {
    const response = await this.get(`${this.basePath}/access-level`);
    return response.data;
  }

  /**
   * Get active access levels only
   */
  async getActiveAccessLevels(): Promise<ApiResponse<AccessLevel[]>> {
    const response = await this.get(`${this.basePath}/access-level/active`);
    return response.data;
  }

  /**
   * Get access level by ID
   */
  async getAccessLevelById(id: number): Promise<ApiResponse<AccessLevel>> {
    const response = await this.get(`${this.basePath}/access-level/${id}`);
    return response.data;
  }

  /**
   * Get access level by code
   */
  async getAccessLevelByCode(code: string): Promise<ApiResponse<AccessLevel>> {
    const response = await this.get(`${this.basePath}/access-level/code/${code}`);
    return response.data;
  }

  /**
   * Create or update access level
   */
  async saveAccessLevel(data: AccessLevelInput): Promise<ApiResponse<AccessLevel>> {
    const response = await this.post(`${this.basePath}/access-level`, data);
    return response.data;
  }

  /**
   * Delete access level
   */
  async deleteAccessLevel(id: number): Promise<ApiResponse<AccessLevel>> {
    const response = await this.delete(`${this.basePath}/access-level/${id}`);
    return response.data;
  }

  // ============================================================================
  // ARTICLE TRANSACTION APIs
  // ============================================================================

  /**
   * Get all articles
   */
  async getAllArticles(): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/article`);
    return response.data;
  }

  /**
   * Get article by ID
   */
  async getArticleById(id: number): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/article/${id}`);
    return response.data;
  }

  /**
   * Get my articles (articles created by current user)
   */
  async getMyArticles(): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/article/my`);
    return response.data;
  }

  /**
   * Get top articles by views
   */
  async getTopArticles(limit: number = 5): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/article/top?limit=${limit}`);
    return response.data;
  }

  /**
   * Get trending articles
   */
  async getTrendingArticles(limit: number = 5): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/article/trending?limit=${limit}`);
    return response.data;
  }

  /**
   * Get recent articles
   */
  async getRecentArticles(limit: number = 10): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/article/recent?limit=${limit}`);
    return response.data;
  }

  /**
   * Create or update article
   */
  async saveArticle(data: any): Promise<ApiResponse<any>> {
    const response = await this.post(`${this.basePath}/article`, data);
    return response.data;
  }

  /**
   * Delete article (soft delete)
   */
  async deleteArticle(id: number): Promise<ApiResponse<any>> {
    const response = await this.delete(`${this.basePath}/article/${id}`);
    return response.data;
  }

  // ============================================================================
  // DASHBOARD APIs
  // ============================================================================

  /**
   * Get dashboard statistics
   */
  async getDashboardStats(): Promise<ApiResponse<any>> {
    const response = await this.get(`${this.basePath}/dashboard/stats`);
    return response.data;
  }

  /**
   * Get top contributors
   */
  async getTopContributors(limit: number = 5): Promise<ApiResponse<any[]>> {
    const response = await this.get(`${this.basePath}/dashboard/top-contributors?limit=${limit}`);
    return response.data;
  }

  /**
   * Get department curators (all or by department)
   */
  async getDepartmentCurators(dptId?: number): Promise<ApiResponse<any[]>> {
    const url = dptId
      ? `${this.basePath}/dashboard/department-curators/${dptId}`
      : `${this.basePath}/dashboard/department-curators`;
    const response = await this.get(url);
    return response.data;
  }
}

// Create and export singleton instance
const dsWikiService = new DSWikiService();
export default dsWikiService;
